/**
 * The §3.1 preference table, as values rather than as scattered string
 * literals.
 *
 * Every entry pairs a key with the type it holds *and* the default the spec
 * states, because those three facts are only correct together. A key read in
 * one place with `false` as the fallback and in another with `true` is a bug
 * that no type checker catches and no test finds unless it happens to read
 * both — and the two places would be a settings screen and the code that acts
 * on the setting, which is the worst possible pair to disagree.
 *
 * Decoding is total. An unrecognized stored value resolves to the default
 * rather than failing, because nothing in this store is user content: a
 * preference is allowed to be lost, and an appearance preference that threw
 * would keep the app off-screen over a value that has a correct answer for
 * every device.
 */

import type { AppearanceMode, CueToggleKey } from "@ff/domain";
import {
  CUE_TOGGLE_DEFAULTS,
  DEFAULT_APPEARANCE_MODE,
  JOURNAL_LOCK_DEFAULT,
  JOURNAL_LOCK_SETTING_KEY,
  resolveAppearanceMode,
} from "@ff/domain";

/**
 * One typed preference: where it lives, what it defaults to, and how it
 * survives a round trip through string storage.
 */
export interface PreferenceDefinition<T> {
  readonly key: string;
  readonly fallback: T;
  /** `undefined` is "never written", which is distinct from a stored `""`. */
  decode(stored: string | undefined): T;
  encode(value: T): string;
}

const TRUE = "true";
const FALSE = "false";

/**
 * Booleans are stored as the two words, not as `1`/`0` or as presence of the
 * key. Presence-as-true cannot represent "the user explicitly turned this off",
 * which for `cues.bells` — default on — is the only state worth recording.
 */
export function booleanPreference(key: string, fallback: boolean): PreferenceDefinition<boolean> {
  return {
    key,
    fallback,
    decode(stored) {
      if (stored === TRUE) return true;
      if (stored === FALSE) return false;
      return fallback;
    },
    encode(value) {
      return value ? TRUE : FALSE;
    },
  };
}

/**
 * A cue toggle, taking both its key *and* its default from `@ff/domain`.
 *
 * `CueToggleKey` is declared there as contract ("storage keys are part of the
 * contract"), and `CUE_TOGGLE_DEFAULTS` is the domain's own answer for what an
 * absent key means. Restating either here would create a second answer that
 * agrees today and drifts the first time the domain changes one — and the
 * symptom would be a returning user's cue silently flipping, which no test in
 * this package would see. The parameter type is the union, so a mistyped key is
 * a compile error rather than a preference that reads as its fallback forever.
 */
function cueTogglePreference(key: CueToggleKey): PreferenceDefinition<boolean> {
  return booleanPreference(key, CUE_TOGGLE_DEFAULTS[key]);
}

/**
 * The complete navigation/onboarding and cue persistence surface from §3.1.
 * Keys are the raw strings; namespacing belongs to the adapter that owns the
 * substrate, not to the table that names the settings.
 *
 * The cue and journal-lock keys are re-exported from `@ff/domain` rather than
 * retyped, so this table names the §3.1 surface without becoming a second place
 * those keys are spelled.
 */
export const PREFERENCE_KEYS = {
  onboardingCompleted: "onboarding.completed.v1",
  lockScreenCues: "cues.lockScreen",
  journalLockEnabled: JOURNAL_LOCK_SETTING_KEY,
  bellCues: "cues.bells",
  voiceCues: "cues.voice",
  hapticCues: "cues.haptics",
  appearanceMode: "appearance.mode",
} as const satisfies Readonly<Record<string, string>> & {
  // Pins the four cue entries to the domain union: a key that stops being a
  // `CueToggleKey` stops compiling here rather than reading as its fallback.
  readonly lockScreenCues: CueToggleKey;
  readonly bellCues: CueToggleKey;
  readonly voiceCues: CueToggleKey;
  readonly hapticCues: CueToggleKey;
};

export const ONBOARDING_COMPLETED = booleanPreference(
  PREFERENCE_KEYS.onboardingCompleted,
  false,
);

export const LOCK_SCREEN_CUES = cueTogglePreference(PREFERENCE_KEYS.lockScreenCues);

/**
 * `JOURNAL_LOCK_DEFAULT` is the domain's statement that the app never flips
 * this by itself. A local `false` here would be a second place that promise is
 * made, and the one that got edited would not be this one.
 */
export const JOURNAL_LOCK_ENABLED = booleanPreference(
  PREFERENCE_KEYS.journalLockEnabled,
  JOURNAL_LOCK_DEFAULT,
);

export const BELL_CUES = cueTogglePreference(PREFERENCE_KEYS.bellCues);

export const VOICE_CUES = cueTogglePreference(PREFERENCE_KEYS.voiceCues);

/**
 * Kept even though the web build has no haptic output. The key is part of the
 * persisted surface and a stored preference that stopped being read would come
 * back wrong the day the platform grows the capability; the *rendering* of a
 * missing output is a Settings decision (F-ST-*), not a storage one.
 */
export const HAPTIC_CUES = cueTogglePreference(PREFERENCE_KEYS.hapticCues);

/**
 * Decoding delegates to `resolveAppearanceMode`, which is the domain's own
 * statement that any unrecognized value resolves to System. Re-implementing
 * that check here would be a second answer to a question that already has one.
 */
export const APPEARANCE_MODE: PreferenceDefinition<AppearanceMode> = {
  key: PREFERENCE_KEYS.appearanceMode,
  fallback: DEFAULT_APPEARANCE_MODE,
  decode: (stored) => resolveAppearanceMode(stored),
  encode: (value) => value,
};

/**
 * Every definition in the table, for code that has to enumerate the surface
 * rather than read one setting — a reset-to-defaults action, or a diagnostic
 * that reports what is stored. Keeping the list beside the definitions means a
 * new preference is added in one place instead of two.
 */
export const PREFERENCE_DEFINITIONS: readonly PreferenceDefinition<unknown>[] = [
  ONBOARDING_COMPLETED,
  LOCK_SCREEN_CUES,
  JOURNAL_LOCK_ENABLED,
  BELL_CUES,
  VOICE_CUES,
  HAPTIC_CUES,
  APPEARANCE_MODE,
];
